import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { BaseTimePeriod, MainFilters, TimePeriod } from '@services/FiltersTypes';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import {
  FactRevenue,
  FeatureOption,
  Filters,
  getPageTypeWithPageName,
  Option,
  PageName,
  PageType,
  ShareType,
  SubSection,
} from '@utils/pageConfig/Types';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { ExploreTrendsStackParams, ExportExploreTrendsStack } from '@data-visualization/trends-stack-chart/Types';
import {
  ExportPerformanceBySegmentResp,
  PerformanceAnalysisParam,
} from '@data-visualization/performance-analysis-line-chart/Types';
import {
  DefaultSortingOptionsForPerformanceAnalysisboard,
  getDisplayNameFromHeaderKey,
  kpiDropdownOptions,
  PerformanceAnalysisboardTableId,
} from '@models/PerformanceAnalysisboard';

import { actionCreators, actionCreatorsMarketPremium } from '../../../state/Index';
import {
  checkIfCountryExist,
  getInitialAppliedCountries,
  isAllFeatureValuesEmpty,
  isValidTimeFrameAndPeriod,
} from '@utils/FilterUtils';
import {
  checkPeriodExist,
  convertYTDOrMATtoPeriodType,
  getDefaultMonthlyPeriodForPALineChart,
  getDefaultValueForDerivablePeriodicity,
  getPeriodicityForMATAndYTD,
  isAggregatedPeriodicity,
} from '@utils/PeriodUtils';

import { CATEGORY_FEATURE_OPTION, getFeatureSelector } from '@utils/hook/selector/ShowPerformanceWithinSelector';
import { matchKPI, usePersistedSorting } from '@utils/hook/sorting/UsePersistedSorting';
import { ProductType } from '../../product-analyzer/Product';

import { ExportFormat, useAnalytics } from '@ecosystem/newron-client-lib';
import { SortFieldDisplayProps, SortingOptionsType } from '@components/design/migrated/Table';
import { displayExportErrorToastMessage, getAppliedFiltersText } from '@hooks/export/ExportUtils';
import {
  ExportLineChartOptions,
  ExportStackChartOptions,
  useExportMultiChart,
} from '@hooks/export/utilities/UseExportMultiChart';
import { getExportTrackingFormat } from '@hooks/export/export-tracking/ExportTracking';
import getComparisonPeriod from '@components/filter-selection-note/GetComparisonPeriod';
import { useToastMessage } from '@components/toast-message/ToastMessage';
import { IRootState } from '@state/reducers/Reducers';
import { getChartSubtitles } from '@utils/KpiTitleUtils';
import saveErrorLog, { ErrorTagEnum } from '@utils/LogUtil';
import { EXPLORE_TRENDS } from '@utils/StringConstants';
import { constructCurrencyLabel } from '@utils/currencyUtils/CurrencyUtils';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  dataExportedTrackingFunction,
  DataExportTrackingElementParam,
  useDataExportTracking,
} from '@utils/page-analytics/model/DataExportTracking';
import {
  EXPLORE_CARD_TITLE,
  EXPLORE_LINE_CHART_TITLE,
  EXPLORE_TRENDS_CARD_TITLE,
  EXPLORE_TRENDS_LINE_CHART_WARNING_PERIODICITY_MESSAGE,
  EXPLORE_TRENDS_STACK_BAR_CHART,
  PERFORMANCE_ANALYSIS_CARD_TITLE,
} from '../../Utilities';
import { byCountry } from '@state/reducers/initial-filter/InitialFiltersPredicates';
import { MainFiltersState } from '@state/reducers/main-filter/MainFiltersReducer';
import {
  checkIsMultiCountryClient,
  checkIsSelectedTimeFrameAndPeriodExistInAllMultiCountries,
  getLatestPeriodAmongAppliedCountries,
} from '@utils/multiCountryUtils/MultiCountryUtils';
import { Country } from '@components/context-bar/Types';
import { modifiedPAORSegmentExportGrandTotalToCamelCaseTotalValue } from '@utils/TotalUtils';
import CategoryOrSegmentsLeaderboardGroupProvider from '@components/category-leaderboard/utils/CategoryOrSegmentsLeaderboardGroupProvider';
import MarketPerformancePosFactsTiles from '@components/market-performance-pos-facts-tiles/MarketPerformancePosFactsTiles';
import ListBoxSelector from '@components/button-menu/ListBoxSelector';
import { TableName } from '@utils/page-analytics/model/TableClickTracking';
import { ExploreTabView } from '@components/tab-nav/TabConfig';
import { ViewType } from '@hooks/export/Model';
import { containers } from '@ecosystem/newron-design-system';
import { SalesPerformance } from '@models/SalesPerformance';
import { getCategoryLeaderboardColumns } from '@components/category-leaderboard/category-leaderboard-table-columns/CategoryLeaderboardTableColumns';
import { FeatureSelectorType } from '@components/feature-selector/Types';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import { PerformanceAnalysisLineChartInsight } from '@components/collections/performance-analysis-line-chart/Model';
import { RetailerKpiOption, SaleType } from '@models/Products';
import { transformShareTypeToRetailerKpiOption } from '@data-visualization/utilities/ChartUtils';
import PerformanceAnalysisLineChartView from '@data-visualization/performance-analysis-line-chart/PerformanceAnalysisLineChartView';
import {
  PAGE_VIEW_TRACKING_TITLE,
  pageViewTrackingFunction,
  PageViewTrackingFunctionParam,
} from '@utils/page-analytics/model/PageViewedTracking';
import { getAbortController } from '@services/utils/OverviewServiceUtils';
import ExploreHeaderIcon from '@pages/explore/header-icon/ExploreHeaderIcon';
import { TrendsLineChart, TrendsStackChart } from '@utils/exploreUtils/ExploreType';
import { ChartKeys } from '@const/ChartKeys';
import { useExportImages } from '@hooks/export/UseExportChartImage';
import { generateUniqueChartKeys } from '@data-visualization/utilities/GenerateUniqueChartKeys';
import {
  processExploreMarketLineChartData,
  processExploreRetailerLineChartData,
} from '@data-visualization/utilities/line-chart/FormatLineChartData';
import { getExploreTrends, getExploreTrendsStackChart } from '@services/explore/Explore.service';
import { LimitPgInfo } from '@pages/LimitPgInfo';
import { WithAlert } from '@components/design';
import { CollectionDataType } from '@ecosystem/newron-client-collections/build/shared/types';
import {
  PAGE_ELEMENT_CLICK_TRACKING_TITLE,
  pageElementClickTrackingFunction,
  PageElementClickTrackingParam,
  TOGGLE_CLICKED_TITLE,
} from '@utils/page-analytics/model/PageElementClickedTracking';
import ExploreTrendsStackChartView from '@data-visualization/trends-stack-chart/ExploreTrendsStackChartView';
import { exportChartKey } from '@data-visualization/trends-stack-chart/StackedChartUtilities';
import { TrendStackBarChartInsight } from '@components/collections/explore-trends-stacked-bar-chart/Model';
import { SortingOptions } from '@components/design/migrated/Table/Sorting/types';
import { ColumnHeaderForCL } from '@models/CategoryLeaderboard';
import { ExploreViewState } from '@models/SavedViews';
import { getStackChartSalesType, trendedStackChartKpiDropdownOptions } from '@models/ExploreTrendsStackChart';
import { getFilteredFeatureValues } from '@utils/FilterFeatureFromDropdown';
import useLimitProductGroup from '@utils/hook/explore/UseLimitProductGroup';

interface Props {
  periodicity: Periodicity;
  period: BaseTimePeriod;
  comparisonPeriod: RelativePeriod | BaseTimePeriod;
  appliedCountries: Option[];
  selectedCountries: Option[];
  appliedProductGroups: Option[];
  defaultFilters: MainFilters[];
  selectedGrowthType: GrowthTypes;
  setGrowthOption: (growthType: GrowthTypes) => void;
  effectiveProductType: ProductType;
  feature: FeatureOption;
  setFeature: Dispatch<SetStateAction<FeatureOption>>;
  selectedShareType: ShareType;
  setSelectedShareType: Dispatch<SetStateAction<ShareType>>;
  selectedAbsoluteType: ShareType;
  setSelectedAbsoluteType: Dispatch<SetStateAction<ShareType>>;
  selectedSaleType: SaleType;
  setSelectedSaleType: Dispatch<SetStateAction<SaleType>>;
  currentSortingOption: SortingOptions;
  setCurrentSortingOption: Dispatch<SetStateAction<SortingOptions>>;
  queryCountries: Country[];
  isLineChartView: boolean;
  setIsLineChartView: Dispatch<SetStateAction<boolean>>;
  allFeatureValues: Filters[];
  factRevenue: FactRevenue;
  volume: string;
  revenuePerformance: SalesPerformance;
  volumePerformance: SalesPerformance;
  loadingPosFacts: boolean;
  isFetchingCardApi: boolean;
  exploreViewState: ExploreViewState;
  isApiError?: boolean;
}

const { Card } = containers;
const Trends: React.FC<Props> = ({
  periodicity,
  period,
  comparisonPeriod,
  appliedCountries,
  selectedCountries,
  appliedProductGroups,
  defaultFilters,
  selectedGrowthType,
  setGrowthOption,
  effectiveProductType,
  feature,
  setFeature,
  selectedShareType,
  setSelectedShareType,
  selectedAbsoluteType,
  setSelectedAbsoluteType,
  selectedSaleType,
  setSelectedSaleType,
  currentSortingOption,
  setCurrentSortingOption,
  queryCountries,
  isLineChartView,
  setIsLineChartView,
  allFeatureValues,
  factRevenue,
  volume,
  revenuePerformance,
  volumePerformance,
  loadingPosFacts,
  isFetchingCardApi,
  exploreViewState,
  isApiError,
}) => {
  const dispatch = useDispatch();
  const pagesConfig = usePageConfig();
  const { track } = useAnalytics();
  const [loadingExploreTrends, setLoadingExploreTrends] = useState<boolean>(true);
  const [loadingExploreTrendsStackChart, setLoadingExploreTrendsStackChart] = useState<boolean>(true);
  const [exploreTrends, setExploreTrends] = useState(new ExportPerformanceBySegmentResp());
  const [exploreTrendsStackChart, setExploreTrendsStackChart] = useState(new ExportExploreTrendsStack());
  const [isFetchingLineChartApi, setIsFetchingLineChartApi] = useState<boolean>(false);
  const [isFetchingStackChartApi, setIsFetchingStackChartApi] = useState<boolean>(false);
  const [isExportButtonDisabled, setIsExportButtonDisabled] = useState<boolean>(false);
  const [isStackChartExportButtonDisabled, setIsStackChartExportButtonDisabled] = useState<boolean>(false);
  const [sortStackedChart, setSortStackedChart] = useState<SortFieldDisplayProps>(
    pagesConfig.sortByOptionsPerformanceAnalysisStackedChart,
  );
  const [areDependenciesUpdated, setAreDependenciesUpdated] = useState<boolean>(false); //This state group all the dependencies that needed for DATA api call and prevent multiple dependencies triggered.
  const [onPageFirstLoad, setOnPageFirstLoad] = useState<boolean>(true);

  const mainFiltersState: MainFiltersState = useSelector((state: IRootState) => state.mainFilters);
  const sideFiltersState = useSelector((state: IRootState) => state.sideFilters);
  const initialFiltersState = useSelector((state: IRootState) => state.initialFilters);
  const retailerClientState = useSelector((state: IRootState) => state.retailerClient);
  const { showToastMessage } = useToastMessage();

  const [trendsParam, setTrendsParam] = useState<PerformanceAnalysisParam>();
  const [stackTrendsParam, setStackTrendsParam] = useState<ExploreTrendsStackParams>();
  const { updateMainFiltersMarketPremium } = bindActionCreators(actionCreatorsMarketPremium, dispatch);
  const { updateMainFilters } = bindActionCreators(actionCreators, dispatch);
  const [isDismissLineChartWarningMessage, setIsDismissLineChartWarningMessage] = useState<boolean>(false);
  const onDismissLineChartWarningMessage = () => {
    setIsDismissLineChartWarningMessage(true);
  };

  const { clearExportImage, exportImages } = useExportImages(PageType.EXPLORE);
  const isLimitPG = useLimitProductGroup(appliedProductGroups.length, feature);
  const createViewState = useCallback(() => exploreViewState, [exploreViewState]);

  const hasInitializedFromPagesConfig = useRef(false);

  useEffect(() => {
    if (!hasInitializedFromPagesConfig.current) {
      const restoredSort = pagesConfig.sortByOptionsPerformanceAnalysisStackedChart;
      if (restoredSort?.sortKey) {
        setSortStackedChart(restoredSort);
        hasInitializedFromPagesConfig.current = true;
      }
    }
  }, [JSON.stringify(pagesConfig.sortByOptionsPerformanceAnalysisStackedChart)]);

  useEffect(() => {
    //Start Page View Amplitude Tracking
    //Update page viewing
    const trackingParam: PageViewTrackingFunctionParam = {
      eventTitle: PAGE_VIEW_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      pageType: PageType.EXPLORE, // Won't get from redux because of async. RO & CL page sharing the same pageType in pagesConfig
      pageName: PageName.EXPLORE, // Won't get from redux because of async
      access: pagesConfig.access,
      initialLocationId: getInitialAppliedCountries(pagesConfig, PageType.EXPLORE, effectiveProductType),
      retailerClientState: retailerClientState,
      subSection: SubSection.EXPLORE_TRENDS,
      track: track,
    };

    setGrowthOption(mainFiltersState.selectedGrowthType);
    pageViewTrackingFunction(trackingParam);
    pagesConfig.setIsNavigateFromRS(true);
    pagesConfig.setIsHidePGInfoWarning(false);
    pagesConfig.setClearFilters(false);
    pagesConfig.setSelectedTimePeriod(period);

    setOnPageFirstLoad(false);
  }, []);

  const appliedCountry = (): Country | undefined => {
    return pagesConfig.clientCountries.filter((x) => appliedCountries.some((y) => x.id === y.value)).pop();
  };

  const [updatedFeatureFilters, setUpdatedFeatureFilters] = useState<Filters[]>(allFeatureValues);
  const removeFeatureFromDropdown = (currentFeatureValues: Filters[]): Filters[] => {
    return getFilteredFeatureValues(currentFeatureValues, pagesConfig, effectiveProductType);
  };

  const constructFeatureSelectorType = (): FeatureSelectorType => ({
    isDisabled: isFetchingCardApi,
    feature: feature,
    setFeature: setFeature,
    featureFilters: updatedFeatureFilters,
  });

  const apiTriggeringValidator = () => {
    const isAppliedFiltersEqualToSelectedFilters =
      JSON.stringify(pagesConfig.appliedFilters[PageType.EXPLORE].selectedFilters) ===
      JSON.stringify(pagesConfig.selectedFilters[PageType.EXPLORE].selectedFilters);
    return (
      isValidTimeFrameAndPeriod(periodicity, period) &&
      appliedProductGroups &&
      checkIfCountryExist(selectedCountries, pagesConfig, effectiveProductType) &&
      (isLineChartView ? !isFetchingLineChartApi : !isFetchingStackChartApi) &&
      !onPageFirstLoad &&
      //to not update upon selected filter change
      isAppliedFiltersEqualToSelectedFilters &&
      !pagesConfig.isClearAllFilterTriggered
    );
  };

  useEffect(() => {}, [pagesConfig.appliedFilters]);

  useEffect(() => {
    setIsExportButtonDisabled(isLineChartView && !exploreTrends.salesRecordsFound);
  }, [isLineChartView, exploreTrends]);

  useEffect(() => {
    setIsStackChartExportButtonDisabled(!isLineChartView && !exploreTrendsStackChart.salesRecordsFound);
  }, [isLineChartView, exploreTrendsStackChart]);

  useEffect(() => {
    pagesConfig.setSortByOptionsPerformanceAnalysisStackedChart(sortStackedChart);
  }, [sortStackedChart]);

  useEffect(() => {
    setAreDependenciesUpdated(true);
    const mainFilters = {
      timeFrame: periodicity,
      period,
      comparisonPeriod,
      selectedGrowthType,
      feature,
    };
    updateMainFiltersMarketPremium({
      ...mainFilters,
    });

    const payload = {
      selectedGrowthType: selectedGrowthType,
      ...(feature !== CATEGORY_FEATURE_OPTION && { feature: feature }),
    };

    updateMainFilters(payload);
  }, [feature, selectedGrowthType]);

  const getKpiParam = (kpi) => {
    return isLineChartView ? kpi.value : kpi; //NOSONAR typescript:S6606
  };
  const getKpi = () => {
    return isLineChartView //NOSONAR typescript:S6606
      ? matchKPI(pagesConfig.sortingOptions, kpiDropdownOptions(volume))
      : getStackChartSalesType(selectedSaleType);
  };
  const constructExploreTrendsParam = (showPerformanceWithin: FeatureOption): PerformanceAnalysisParam => {
    const appliedFilters = [...pagesConfig.appliedFilters[PageType.EXPLORE].selectedFilters];
    let kpi = getKpi();
    return {
      clientId: retailerClientState.clientId,
      productType: effectiveProductType,
      productGroup: appliedProductGroups,
      countries: queryCountries,
      period: period.value,
      comparisonPeriod: comparisonPeriod,
      periodType: periodicity,
      selectedGrowthType: selectedGrowthType,
      saleType: kpi.salesType,
      appliedFilters: appliedFilters,
      kpi: getKpiParam(kpi),
      segment: showPerformanceWithin.feature,
      ...(showPerformanceWithin.dataType !== CATEGORY_FEATURE_OPTION.dataType && {
        dataType: showPerformanceWithin.dataType,
      }),
      isSummary: false,
      dataType: showPerformanceWithin.dataType,
    };
  };

  const loadExploreTrends = async (showPerformanceWithin: FeatureOption) => {
    const responseTimeFrame = checkPeriodExist(defaultFilters, appliedCountry()!.id, periodicity);
    if (responseTimeFrame !== null && !isFetchingLineChartApi) {
      try {
        setLoadingExploreTrends(true);
        const loadTrendsParam = constructExploreTrendsParam(showPerformanceWithin);
        setTrendsParam(loadTrendsParam);
        const exploreTrendsData = await getExploreTrends(loadTrendsParam);
        setExploreTrends(exploreTrendsData);
      } catch (err: any) {
        setExploreTrends(new ExportPerformanceBySegmentResp());
        saveErrorLog(err, 'Explore Trends - Unable to get trends service.', {
          category: ErrorTagEnum.ApiError,
          requestPayload: {
            clientId: retailerClientState.clientId,
            productGroup: appliedProductGroups,
            period: period.value,
            comparisonPeriod: comparisonPeriod,
            periodType: periodicity,
            segment: feature.feature,
          },
        });
        if (err.message === 'Cancel: Operation canceled due to new request.') {
          return;
        }
      } finally {
        // Listen and used to prevent multiple api calling , and set false sequence is important
        setIsFetchingLineChartApi(false);
        setLoadingExploreTrends(false);
        setAreDependenciesUpdated(false);
        clearExportImage();
      }
    }
  };

  const loadExploreStackTrendsChart = async (showPerformanceWithin: FeatureOption) => {
    const responseTimeFrame = checkPeriodExist(defaultFilters, appliedCountry()!.id, periodicity);
    if (responseTimeFrame !== null && !isFetchingStackChartApi) {
      try {
        setLoadingExploreTrendsStackChart(true);
        const loadTrendsParam = constructExploreTrendsParam(showPerformanceWithin);
        setStackTrendsParam({ ...loadTrendsParam, saleFormat: selectedAbsoluteType });
        const exploreTrendsStackChartData = await getExploreTrendsStackChart({
          ...loadTrendsParam,
          saleFormat: selectedAbsoluteType,
        });
        setExploreTrendsStackChart(exploreTrendsStackChartData);
      } catch (err: any) {
        setExploreTrendsStackChart(new ExportExploreTrendsStack());
        saveErrorLog(err, 'Explore Trends - Unable to get trends service.', {
          category: ErrorTagEnum.ApiError,
          requestPayload: {
            clientId: retailerClientState.clientId,
            productGroup: appliedProductGroups,
            period: period.value,
            comparisonPeriod: comparisonPeriod,
            periodType: periodicity,
            segment: feature.feature,
          },
        });
        if (err.message === 'Cancel: Operation canceled due to new request.') {
          return;
        }
      } finally {
        // Listen and used to prevent multiple api calling , and set false sequence is important
        setIsFetchingStackChartApi(false);
        setLoadingExploreTrendsStackChart(false);
        setAreDependenciesUpdated(false);
        clearExportImage();
      }
    }
  };

  const setDataStateToDefault = () => {
    setExploreTrends(new ExportPerformanceBySegmentResp());
    setExploreTrendsStackChart(new ExportExploreTrendsStack());
    setLoadingExploreTrends(false);
    setLoadingExploreTrendsStackChart(false);
    setAreDependenciesUpdated(false);
  };

  useEffect(() => {
    const fetchFeatureValues = async () => {
      if (isLimitPG) {
        // reset all data & pagination & dropdown
        const newFeatureFilters = removeFeatureFromDropdown(allFeatureValues);
        setUpdatedFeatureFilters(newFeatureFilters);
        setDataStateToDefault();
        return;
      }

      const isMonthlyOrWeeklyPeriodicity: boolean = [Periodicity.Monthly, Periodicity.Weekly].includes(periodicity);

      //To check if feature values not empty, then not required to call PA board, stack chart and line chart API
      //we should only call line chart api for Monthly and weekly periodicity
      //hence we have the additional checking for periodicity before call the line chart api
      if (!isAllFeatureValuesEmpty(allFeatureValues) && isMonthlyOrWeeklyPeriodicity) {
        const newFeatureFilters = removeFeatureFromDropdown(allFeatureValues);
        setUpdatedFeatureFilters(newFeatureFilters);

        const showPerformanceWithinOption = getFeatureSelector(newFeatureFilters, feature, [], pagesConfig.pageName);
        setFeature(showPerformanceWithinOption);

        const controller = getAbortController();
        if (isLineChartView) {
          await loadExploreTrends(showPerformanceWithinOption);
        } else {
          await loadExploreStackTrendsChart(showPerformanceWithinOption);
        }

        return () => {
          controller.abort();
          setIsFetchingLineChartApi(false);
          setIsFetchingStackChartApi(false);
          setLoadingExploreTrends(false);
          setLoadingExploreTrendsStackChart(false);
        };
        //we should only call line chart api for Monthly and weekly periodicity
        //hence we have the additional checking for periodicity before set all the data to default
        //isAggregatedPeriodicity is used to check if the periodicity is not monthly or weekly
      } else if (
        (allFeatureValues.length > 0 && isAllFeatureValuesEmpty(allFeatureValues)) ||
        isAggregatedPeriodicity(periodicity, period.value)
      ) {
        setDataStateToDefault();
      }
    };
    pagesConfig.setIsCountryChanged(false);
    if (areDependenciesUpdated) {
      fetchFeatureValues();
    } else {
      const newFeatureFilters = removeFeatureFromDropdown(allFeatureValues);
      setUpdatedFeatureFilters(newFeatureFilters);
    }
  }, [areDependenciesUpdated, allFeatureValues, isLineChartView]);

  const featureHandler = React.useCallback(() => {
    setAreDependenciesUpdated(true);
  }, [allFeatureValues]);

  const { sorting, updateOptions } = usePersistedSorting({
    tableId: PerformanceAnalysisboardTableId,
    defaultSorting: DefaultSortingOptionsForPerformanceAnalysisboard,
  });

  useEffect(() => {
    if (!areDependenciesUpdated && apiTriggeringValidator() && !isAllFeatureValuesEmpty(allFeatureValues)) {
      setAreDependenciesUpdated(true);
    }
    //This is to stop the tables and charts to rerender upon clearing the filters.
    pagesConfig.setIsClearAllFilterTriggered(false);
  }, [
    periodicity,
    period,
    comparisonPeriod,
    pagesConfig.sortingOptions,
    pagesConfig.sortingOptionsPerformanceAnalysisStackedChart,
    selectedGrowthType,
    allFeatureValues,
    feature,
    onPageFirstLoad,
    isFetchingLineChartApi,
    isFetchingStackChartApi,
    isLineChartView,
  ]);

  const shareTypeRef = useRef(ShareType.MarketShare);
  shareTypeRef.current = selectedShareType;

  const [selectedRetailerOption, setSelectedRetailerOption] = useState<RetailerKpiOption>(
    transformShareTypeToRetailerKpiOption(selectedShareType),
  );

  const isMultiCountry = useMemo((): boolean => {
    return checkIsMultiCountryClient(pagesConfig.clientId);
  }, [pagesConfig.clientId]);

  const updatedInitialFiltersState = useMemo(
    () => ({
      ...initialFiltersState,
      initialFilters: initialFiltersState.initialFilters.map(
        (filter: { timePeriod: TimePeriod; timePeriodV2: TimePeriod }) => ({
          ...filter,
          timePeriod: filter.timePeriod,
        }),
      ),
    }),
    [initialFiltersState],
  );

  const timePeriodOptions: TimePeriod = useMemo(() => {
    return isMultiCountry && appliedCountries.length > 1
      ? getLatestPeriodAmongAppliedCountries(appliedCountries, updatedInitialFiltersState)
      : updatedInitialFiltersState.initialFilters.filter(byCountry(appliedCountries[0].value))[0].timePeriod;
  }, [appliedCountries, initialFiltersState, isMultiCountry, stackTrendsParam, trendsParam]);

  const noResultHandler = React.useCallback(() => {
    setIsExportButtonDisabled(true);
  }, [exploreTrends]);

  const noResultHandlerStackChart = React.useCallback(() => {
    setIsStackChartExportButtonDisabled(true);
  }, [exploreTrendsStackChart]);

  useEffect(() => {
    setSelectedRetailerOption(transformShareTypeToRetailerKpiOption(selectedShareType));
  }, [selectedShareType]);

  useEffect(() => {
    setAreDependenciesUpdated(true);
  }, [selectedSaleType, selectedAbsoluteType]);

  const renderLineChart = () => {
    return isLimitPG ? (
      LimitPgInfo()
    ) : (
      <PerformanceAnalysisLineChartView
        data={exploreTrends}
        selectedPeriodicity={periodicity}
        factRevenue={factRevenue}
        loadingLineChart={loadingExploreTrends}
        selectedShareType={selectedShareType}
        onChangeSelectedShareType={selectedShareType}
        featureSelectorType={constructFeatureSelectorType()}
        featureHandler={featureHandler}
        noResultHandler={noResultHandler}
        volume={volume}
        selectedRetailerOption={selectedRetailerOption}
        setSelectedRetailerOption={setSelectedRetailerOption}
        selectedLineChartSaleType={selectedSaleType}
        setSelectedLineChartSaleType={setSelectedSaleType}
        noPeriodExistForMulticountry={
          !checkIsSelectedTimeFrameAndPeriodExistInAllMultiCountries(
            pagesConfig.appliedFilters,
            mainFiltersState,
            initialFiltersState,
            PageType.EXPLORE,
          )
        }
        isExplorePage={true}
        trackingTitle={EXPLORE_LINE_CHART_TITLE}
        subSectionForAmplitudeTracking={SubSection.EXPLORE_TRENDS}
      />
    );
  };

  const renderStackChart = () => {
    return isLimitPG ? (
      LimitPgInfo()
    ) : (
      <ExploreTrendsStackChartView
        data={exploreTrendsStackChart}
        selectedPeriodicity={periodicity}
        factRevenue={factRevenue}
        selectedGrowthType={selectedGrowthType}
        loadingExploreTrendsStackChart={loadingExploreTrendsStackChart}
        selectedShareType={selectedShareType}
        onChangeSelectedShareType={selectedShareType}
        onChangeSelectedAbsoluteType={selectedAbsoluteType}
        featureSelectorType={constructFeatureSelectorType()}
        featureHandler={featureHandler}
        noResultHandler={noResultHandlerStackChart}
        volume={volume}
        selectedRetailerOption={selectedRetailerOption}
        setSelectedRetailerOption={setSelectedRetailerOption}
        selectedSaleType={selectedSaleType}
        setSelectedSaleType={setSelectedSaleType}
        noPeriodExistForMulticountry={
          !checkIsSelectedTimeFrameAndPeriodExistInAllMultiCountries(
            pagesConfig.appliedFilters,
            mainFiltersState,
            initialFiltersState,
            PageType.EXPLORE,
          )
        }
        isExplorePage={true}
        trackingTitle={EXPLORE_TRENDS_STACK_BAR_CHART}
        stackedChartSortingOrder={sortStackedChart}
        subSectionForAmplitudeTracking={SubSection.EXPLORE_TRENDS}
      />
    );
  };

  const { download: exportLineChart } = useExportMultiChart();
  const { download: exportStackChart } = useExportMultiChart();

  const handleDataExportTrackingFunction = useDataExportTracking('mainFilters');

  const handleExportedTracking = (
    elementType: ElementType,
    exportFormats: ExportFormat[],
    elementKpi?: string,
    elementSort?: SortingOptionsType,
  ) => {
    const dataExportedTrackingParam: DataExportTrackingElementParam = {
      elementType,
      elementKpi,
      elementSort,
      optionChosen: exportFormats.map((format) => getExportTrackingFormat(format)),
    };
    handleDataExportTrackingFunction(dataExportedTrackingParam, dataExportedTrackingFunction);
  };

  const onChartDownload = useCallback(
    async (exportFormats: ExportFormat[]): Promise<void> => {
      if (!isLineChartView) await downloadStackChart(exportFormats);
      if (!trendsParam) {
        return;
      }

      const periodType = getPeriodicityForMATAndYTD(period, periodicity).periodicity;
      const requestPayload = {
        clientId: retailerClientState.clientId,
        period: period.value,
        productGroup: appliedProductGroups,
        comparisonPeriod: comparisonPeriod,
        periodType: periodicity,
        segment: feature.feature,
      };

      try {
        const data: ExportPerformanceBySegmentResp = await getExploreTrends(trendsParam);
        const modifiedExportPerformanceBySegmentKpi = modifiedPAORSegmentExportGrandTotalToCamelCaseTotalValue(data);

        //Use to get default Relative Period for Custom months, Rolling Quarter and Calendar Quarter
        //For Custom months, default relative period is Previous Year Current Period
        const relativeComparisonPeriod =
          periodType === Periodicity.CustomMonths
            ? RelativePeriod.PreviousYearCurrentPeriod
            : (comparisonPeriod as RelativePeriod);

        //For custom months, rolling quarter and calendar quarter, default period will be monthly latest period
        const lineChartTimePeriod = getDefaultMonthlyPeriodForPALineChart(periodicity, period, timePeriodOptions);

        //For custom months, rolling quarter and calendar quarter, default periodType will be monthly
        const lineChartPeriodType = convertYTDOrMATtoPeriodType(
          getDefaultValueForDerivablePeriodicity(periodType),
          period.label,
        );

        //For custom months, rolling quarter and calendar quarter, default comparison period will be same period last year
        const lineChartComparisonTimePeriod = getComparisonPeriod(
          lineChartTimePeriod,
          lineChartPeriodType,
          relativeComparisonPeriod,
        );

        const getSelectedSaleType = Object.values(SaleType).includes(selectedSaleType)
          ? selectedSaleType
          : Object.values(SaleType)[0];

        const marketData = processExploreMarketLineChartData(
          modifiedExportPerformanceBySegmentKpi.kpiDataDto,
          getSelectedSaleType,
        ).filter(Boolean);
        const retailerData = processExploreRetailerLineChartData(
          modifiedExportPerformanceBySegmentKpi.kpiDataDto,
          getSelectedSaleType,
          selectedRetailerOption,
        ).filter(Boolean);

        const exportImage = [
          exportImages[generateUniqueChartKeys(marketData, ChartKeys[PageType.EXPLORE].MARKET)],
          exportImages[generateUniqueChartKeys(retailerData, ChartKeys[PageType.EXPLORE].RETAILER)],
        ];

        const options: ExportLineChartOptions = {
          exportFormats,
          title: EXPLORE_TRENDS_CARD_TITLE,
          timePeriod: lineChartTimePeriod.value,
          comparisonTimePeriod: lineChartComparisonTimePeriod,
          factRevenue: factRevenue,
          appliedCountry: appliedCountries,
          appliedPg: appliedProductGroups,
          data: modifiedExportPerformanceBySegmentKpi,
          exportImage,
          periodType: lineChartPeriodType,
          showPerformanceWithin: feature.value,
          appliedFiltersText: getAppliedFiltersText(pagesConfig, PageType.PERFORMANCE_ANALYSIS),
          selectedKpi: matchKPI(pagesConfig.sortingOptions, kpiDropdownOptions(volume)),
          pageName: PageName.EXPLORE,
          volume,
          selectedRetailerOption: selectedRetailerOption,
          chartTitles: [EXPLORE_TRENDS, EXPLORE_TRENDS],
          chartSubTitles: getChartSubtitles(
            matchKPI(pagesConfig.sortingOptions, kpiDropdownOptions(volume)).label,
            shareTypeRef.current,
            constructCurrencyLabel(factRevenue),
          ),
        };

        await exportLineChart(options);

        handleExportedTracking(ElementType.LINE_CHART, exportFormats);
      } catch (err: any) {
        const errorMessage = 'Explore trends - get export line chart API error.';
        displayExportErrorToastMessage(err, errorMessage, requestPayload, showToastMessage);
        return;
      }
    },
    [trendsParam, stackTrendsParam, exportImages, sortStackedChart, selectedSaleType, isLineChartView],
  );

  const downloadStackChart = async (exportFormats: ExportFormat[]) => {
    if (!stackTrendsParam) return;
    const periodType = getPeriodicityForMATAndYTD(period, periodicity).periodicity;
    const requestPayload = {
      clientId: retailerClientState.clientId,
      period: period.value,
      productGroup: appliedProductGroups,
      comparisonPeriod: comparisonPeriod,
      periodType: periodicity,
      segment: feature.feature,
    };

    try {
      const [data, stackTrendChartExcelData] = await Promise.all([
        getExploreTrendsStackChart(stackTrendsParam),
        exportFormats.includes(ExportFormat.Xlsx)
          ? getExploreTrendsStackChart(stackTrendsParam, true)
          : Promise.resolve(undefined),
      ]);

      //Use to get default Relative Period for Custom months, Rolling Quarter and Calendar Quarter
      //For Custom months, default relative period is Previous Year Current Period
      const relativeComparisonPeriod =
        periodType === Periodicity.CustomMonths
          ? RelativePeriod.PreviousYearCurrentPeriod
          : (comparisonPeriod as RelativePeriod);

      const lineChartTimePeriod = getDefaultMonthlyPeriodForPALineChart(periodicity, period, timePeriodOptions);

      const lineChartPeriodType = convertYTDOrMATtoPeriodType(
        getDefaultValueForDerivablePeriodicity(periodType),
        period.label,
      );

      const lineChartComparisonTimePeriod = getComparisonPeriod(
        lineChartTimePeriod,
        lineChartPeriodType,
        relativeComparisonPeriod,
      );

      const exportImage = [
        exportImages[exportChartKey(data, selectedAbsoluteType, selectedSaleType, true, false)],
        exportImages[exportChartKey(data, selectedAbsoluteType, selectedSaleType, false, false)],
      ];

      const selectedStackChartKPI = trendedStackChartKpiDropdownOptions.find(
        (e: { value: any }) => e.value === currentSortingOption.headerKey,
      );

      const options: ExportStackChartOptions = {
        exportFormats,
        title: EXPLORE_TRENDS_CARD_TITLE,
        timePeriod: lineChartTimePeriod.value,
        comparisonTimePeriod: lineChartComparisonTimePeriod,
        factRevenue: factRevenue,
        appliedCountry: appliedCountries,
        appliedPg: appliedProductGroups,
        data: data,
        stackTrendChartExcelData,
        exportImage,
        periodType: lineChartPeriodType,
        showPerformanceWithin: feature.value,
        appliedFiltersText: getAppliedFiltersText(pagesConfig, PageType.EXPLORE),
        selectedKpi: selectedStackChartKPI || trendedStackChartKpiDropdownOptions[0],
        sortedBy: sortStackedChart,
        pageName: PageName.EXPLORE,
        volume,
        selectedRetailerOption: selectedRetailerOption,
        chartTitles: [EXPLORE_TRENDS, EXPLORE_TRENDS],
        chartSubTitles: ['Market', 'Retail'],
      };

      await exportStackChart(options);

      handleExportedTracking(ElementType.STACKED_CHART, exportFormats);
    } catch (err: any) {
      const errorMessage = 'Explore trends - get export line chart API error.';
      displayExportErrorToastMessage(err, errorMessage, requestPayload, showToastMessage);
      return;
    }
  };

  const getTrendsChartCollectionParams = (): PerformanceAnalysisParam => {
    let kpi = getKpi();
    const appliedFilters = [...pagesConfig.appliedFilters[PageType.EXPLORE].selectedFilters];
    return {
      clientId: retailerClientState.clientId,
      productType: effectiveProductType,
      productGroup: appliedProductGroups,
      countries: pagesConfig.clientCountries.filter((x) => appliedCountries.some((y) => x.id === y.value)),
      period: period.value,
      comparisonPeriod: comparisonPeriod,
      periodType: periodicity,
      selectedGrowthType: selectedGrowthType,
      saleType: kpi.salesType,
      appliedFilters: appliedFilters,
      kpi: getKpiParam(kpi),
      segment: feature.feature,
      dataType: feature.dataType,
      isSummary: false,
      pageName: pagesConfig.pageName,
    };
  };

  const handleViewChange = (view: ViewType) => {
    setIsLineChartView(view === ViewType.LineChart);
    handleElementClickTracking(view);
  };

  const handleElementClickTracking = (title: string) => {
    const params: PageElementClickTrackingParam = {
      sideFilters: sideFiltersState.appliedFilters,
      eventTitle: PAGE_ELEMENT_CLICK_TRACKING_TITLE,
      retailerClientState: retailerClientState,
      chartName: EXPLORE_CARD_TITLE,
      optionChosen: title,
      elementName: TOGGLE_CLICKED_TITLE,
      pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
      pageName: retailerClientState.pageViewing,
      access: pagesConfig.access,
      track: track,
      subSection: SubSection.EXPLORE_TRENDS,
    };
    pageElementClickTrackingFunction(params);
  };

  const createLineChartInsightDataFn: CreateInsightDataFn<PerformanceAnalysisLineChartInsight> = (
    collectionDataType: CollectionDataType = 'live',
  ) => {
    const trendsChartCollectionsApiParamProps = getTrendsChartCollectionParams();
    const getSelectedSaleType = Object.values(SaleType).includes(selectedSaleType)
      ? selectedSaleType
      : Object.values(SaleType)[0];

    return {
      apiParams: {
        ...trendsChartCollectionsApiParamProps,
        kpi: getSelectedSaleType,
      },
      insightCardTitle: PERFORMANCE_ANALYSIS_CARD_TITLE,
      selectedLineChartSaleType: getSelectedSaleType,
      selectedRetailerKpiOption: selectedRetailerOption,
      selectedFeature: feature,
      currencySymbol: factRevenue.currencySymbol,
      retailerClientName: retailerClientState.clientName,
      collectionDataType: collectionDataType,
    };
  };

  const createTrendStackBarChartInsightDataFn: CreateInsightDataFn<TrendStackBarChartInsight> = (
    collectionDataType: CollectionDataType = 'live',
  ) => {
    const trendsChartCollectionsApiParamProps = getTrendsChartCollectionParams();
    const getSelectedSaleTypeLabel = trendedStackChartKpiDropdownOptions.find(
      (o) => o.value === selectedSaleType,
    )?.value;
    return {
      apiParams: {
        ...trendsChartCollectionsApiParamProps,
      },
      insightCardTitle: PERFORMANCE_ANALYSIS_CARD_TITLE,
      selectedRetailerKpiOption: selectedRetailerOption,
      selectedGrowthType: selectedGrowthType,
      selectedFeature: feature,
      currencySymbol: factRevenue.currencySymbol,
      retailerClientName: retailerClientState.clientName,
      collectionDataType: collectionDataType,
      selectedSaleType: getSelectedSaleTypeLabel,
      selectedAbsoluteType: selectedAbsoluteType,
    };
  };

  const currencyLabel = constructCurrencyLabel(factRevenue);

  const defaultSaleTypeOptions = isLineChartView
    ? [
        {
          label: `Revenue${currencyLabel}`,
          value: SaleType.revenue,
        },
        {
          label: volume,
          value: SaleType.volume,
        },
        {
          label: `Price${currencyLabel}`,
          value: SaleType.price,
        },
      ]
    : trendedStackChartKpiDropdownOptions.map((item) => ({
        label: item.label,
        value: item.value,
      }));

  const kpiData = getCategoryLeaderboardColumns(ShareType.MarketShare, volume);

  const shouldDisableChartPickers = () => {
    return isLineChartView //NOSONAR typescript:S2301
      ? isFetchingLineChartApi || loadingExploreTrends //NOSONAR typescript:S6606
      : isFetchingStackChartApi || loadingExploreTrendsStackChart; //NOSONAR typescript:S6606
  };

  useEffect(() => {
    if (!isLineChartView && !isAggregatedPeriodicity(periodicity, period.value)) {
      const getInitialSaleType =
        trendedStackChartKpiDropdownOptions.find((o) => o.value === currentSortingOption.headerKey)?.value ??
        trendedStackChartKpiDropdownOptions[0].value;
      setSelectedSaleType(getInitialSaleType);
      setSortStackedChart({
        sortDirection: currentSortingOption.sortDir,
        sortKey: currentSortingOption.sortKey,
        label: currentSortingOption.label,
      });
    }
  }, []);

  useEffect(() => {
    if (!isLineChartView) {
      const updateCurrentSortingCarryOver = {
        headerKey: selectedSaleType === SaleType.revenue ? ColumnHeaderForCL.marketRevenue : selectedSaleType,
        sortDir: sortStackedChart.sortDirection,
        sortKey: sortStackedChart.sortKey,
        label: sortStackedChart.label,
      };
      setCurrentSortingOption(updateCurrentSortingCarryOver);
    }
  }, [sortStackedChart, selectedSaleType]);

  return (
    <CategoryOrSegmentsLeaderboardGroupProvider>
      <div className="pt-6">
        {!onPageFirstLoad && (
          <MarketPerformancePosFactsTiles
            revenuePerformance={revenuePerformance}
            volumePerformance={volumePerformance}
            selectedGrowthType={selectedGrowthType}
            isLoading={loadingPosFacts}
            factRevenue={factRevenue}
            volume={volume}
            subSectionForAmplitudeTracking={SubSection.EXPLORE_TRENDS}
            isApiError={isApiError}
          />
        )}
        <br />
        {
          <Card testId={EXPLORE_CARD_TITLE}>
            {!isDismissLineChartWarningMessage && isAggregatedPeriodicity(periodicity, period.value) && (
              <div className="my-4" data-testid="periodicity-alert-warning-message">
                <WithAlert
                  title={EXPLORE_TRENDS_LINE_CHART_WARNING_PERIODICITY_MESSAGE}
                  onClick={onDismissLineChartWarningMessage}
                />
              </div>
            )}
            <Card.Title>
              <Card.Content>
                <ListBoxSelector
                  isDisabled={shouldDisableChartPickers()}
                  chartName={TableName.EXPLORE_TRENDS}
                  getDisplayName={getDisplayNameFromHeaderKey}
                  factRevenue={factRevenue}
                  sorting={sorting}
                  kpisConfig={kpiData}
                  updateSorting={updateOptions}
                  featureFilters={updatedFeatureFilters}
                  showPerformanceWithin={feature}
                  featureSelectorType={constructFeatureSelectorType()}
                  volume={volume}
                  isStackedChart={!isLineChartView}
                  tabView={ExploreTabView.Trends}
                  viewType={isLineChartView ? ViewType.LineChart : ViewType.StackedChart}
                  selectedShareType={selectedShareType}
                  setSelectedShareType={setSelectedShareType}
                  selectedSaleType={selectedSaleType}
                  setSelectedSaleType={setSelectedSaleType}
                  selectedAbsoluteType={selectedAbsoluteType}
                  setSelectedAbsoluteType={setSelectedAbsoluteType}
                  defaultSaleTypeOptions={defaultSaleTypeOptions}
                  sortByStackedChart={sortStackedChart}
                  setSortByStackedChart={setSortStackedChart}
                />
              </Card.Content>
              <Card.Title.Actions>
                <ExploreHeaderIcon
                  key={isLineChartView ? 'Trends Line Chart' : 'Trends Stack Chart'}
                  onChartDownload={onChartDownload}
                  isExportButtonDisabled={isExportButtonDisabled}
                  isStackChartExportButtonDisabled={isStackChartExportButtonDisabled}
                  volume={volume}
                  createInsightDataFn={
                    isLineChartView ? createLineChartInsightDataFn : createTrendStackBarChartInsightDataFn
                  }
                  onChange={handleViewChange}
                  exploreChartView={
                    isLineChartView
                      ? ('Trends Line Chart' as TrendsLineChart)
                      : ('Trends Stack Chart' as TrendsStackChart)
                  }
                  exploreTabView={ExploreTabView.Trends}
                  createViewStateFn={createViewState}
                />
              </Card.Title.Actions>
            </Card.Title>
            {isLineChartView ? renderLineChart() : renderStackChart()}
          </Card>
        }
      </div>
    </CategoryOrSegmentsLeaderboardGroupProvider>
  );
};

export default Trends;
